import React, { useRef, useState } from 'react';
import { Form, Typography, Row, Col, Input } from 'antd';
import { v1 as uuid } from 'uuid';

import setNotification from '../../utils/setNotification.utils';
import states from '../../states';
import { useRecoilState } from 'recoil';

import ModalForm from '../ModalForm';

const WriteInExerciseFormModal = ({ handleWriteInExercise }) => {
  const [parametersError, setParametersError] = useState(false);

  const [modals, setModals] = useRecoilState(states.modal);

  const formRef = useRef();

  const handleWriteInExerciseModal = () => {
    formRef.current.resetFields();
    setModals(prevState => ({
      ...prevState,
      writeInExercise: !prevState.writeInExercise,
    }));
  };

  const handleSubmit = value => {
    handleWriteInExercise({
      ...value,
      perdiem: null,
      frequency: null,
      id: uuid(),
    });

    formRef.current.resetFields();

    setNotification('success', 'Success!', 'Write-in exercise has been added.');

    setModals(prevState => ({
      ...prevState,
      writeInExercise: !prevState.writeInExercise,
    }));
  };

  if (!modals.writeInExercise) return null;

  return (
    <ModalForm
      header="Write-In Exercise"
      form="writeInExerciseForm"
      showModal
      handleClose={handleWriteInExerciseModal}
      handleSubmit={handleSubmit}
      width={700}
    >
      <Form
        id="writeInExerciseForm"
        ref={formRef}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={[16, 0]}>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name="exerciseName"
              label="Exercise Name"
              rules={[
                { required: true, message: 'Please input your exercise name.' },
                { max: 256, message: 'Max length is 256 only.' },
              ]}
            >
              <Input
                type="text"
                size="large"
                placeholder="Enter exercise name"
                name="exerciseName"
              />
            </Form.Item>
          </Col>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Typography.Text strong>Parameters</Typography.Text>
            <Row gutter={[16, 0]}>
              <Col lg={8} md={8} sm={24} xs={24}>
                <Form.Item
                  name="sets"
                  label="Sets"
                  dependencies={['reps', 'hold']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value &&
                          !getFieldValue('reps') &&
                          !getFieldValue('hold')
                        ) {
                          setParametersError(true);
                          return Promise.reject();
                        } else {
                          setParametersError(false);
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <Input
                    type="text"
                    size="large"
                    placeholder="Enter sets"
                    name="sets"
                  />
                </Form.Item>
              </Col>
              <Col lg={8} md={8} sm={24} xs={24}>
                <Form.Item
                  name="reps"
                  label="Reps"
                  dependencies={['sets', 'hold']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value &&
                          !getFieldValue('sets') &&
                          !getFieldValue('hold')
                        ) {
                          setParametersError(true);
                          return Promise.reject();
                        } else {
                          setParametersError(false);
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <Input
                    type="text"
                    size="large"
                    placeholder="Enter reps"
                    name="reps"
                  />
                </Form.Item>
              </Col>
              <Col lg={8} md={8} sm={24} xs={24}>
                <Form.Item
                  name="hold"
                  label="Hold"
                  dependencies={['sets', 'reps']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value &&
                          !getFieldValue('sets') &&
                          !getFieldValue('reps')
                        ) {
                          setParametersError(true);
                          return Promise.reject();
                        } else {
                          setParametersError(false);
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <Input
                    type="text"
                    size="large"
                    placeholder="Enter hold"
                    name="hold"
                  />
                </Form.Item>
              </Col>
            </Row>
            {parametersError && (
              <Typography.Paragraph type="danger" style={{ marginTop: -20 }}>
                Please fill in at least one parameter field.
              </Typography.Paragraph>
            )}
          </Col>
          <Col lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name="instructions"
              label="Instructions"
              rules={[
                {
                  required: true,
                  message: 'Please input your instructions.',
                },
                { max: 750, message: 'Max length is 750 only.' },
              ]}
            >
              <Input.TextArea
                size="large"
                placeholder="Enter instructions"
                name="instructions"
                rows={4}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ModalForm>
  );
};

export default WriteInExerciseFormModal;
